import { useState } from "react";
import { Link } from "@/lib/router-compat";
import logoLight from "@/assets/logo-jiia-light.png";
import { analytics } from "@/lib/analytics";
import { toast } from "@/hooks/use-toast";

const Footer = () => {
  const [email, setEmail] = useState("");
  const [subscribing, setSubscribing] = useState(false);
  const [subscribed, setSubscribed] = useState(false);

  const handleSubscribe = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) return;
    setSubscribing(true);
    try {
      const { supabase } = await import("@/integrations/supabase/client");
      const { error } = await supabase
        .from("newsletter_subscribers" as any)
        .insert({ email: email.trim() } as any);
      if (error) {
        if (error.code === "23505") {
          toast({ title: "Vous êtes déjà inscrit(e) ! 💌" });
        } else {
          throw error;
        }
      } else {
        toast({ title: "Bienvenue dans la newsletter ! ✨" });
        // Send welcome email
        supabase.functions.invoke("notify-newsletter-welcome", {
          body: { email: email.trim() },
        }).catch(console.error);
      }
      setSubscribed(true);
      setEmail("");
    } catch (err: any) {
      toast({ title: "Erreur", description: err.message, variant: "destructive" });
    } finally {
      setSubscribing(false);
    }
  };
  return (
    <footer className="bg-primary text-primary-foreground">
      <div className="container py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          <div>
            <img src={logoLight} alt="JIIA Studio" className="h-16 mb-4" width={91} height={64} />
            <p className="text-primary-foreground/70 text-sm leading-relaxed font-body">
              Architecture d'intérieur<br />
              Charline Jacob
            </p>
          </div>

          <div>
            <h4 className="font-body text-xs uppercase tracking-widest mb-4 text-primary-foreground/75">Navigation</h4>
            <ul className="space-y-2">
              {[
                { label: "Prestations", href: "/prestations" },
                { label: "Projets", href: "/projets" },
                { label: "À propos", href: "/a-propos" },
                { label: "Blog", href: "/blog" },
                { label: "FAQ", href: "/prestations#faq" },
                { label: "Contact", href: "/contact" },
              ].map((link) => (
                <li key={link.href}>
                  <Link
                    to={link.href}
                    className="text-sm text-primary-foreground/70 hover:text-primary-foreground transition-colors font-body"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="font-body text-xs uppercase tracking-widest mb-4 text-primary-foreground/75">Zones d'intervention</h4>
            <ul className="space-y-2">
              {[
                { label: "Lyon", href: "/architecte-interieur-lyon" },
                { label: "Dardilly", href: "/architecte-interieur-dardilly" },
                { label: "Écully", href: "/architecte-interieur-ecully" },
                { label: "Caluire-et-Cuire", href: "/architecte-interieur-caluire-et-cuire" },
                { label: "Tassin-la-Demi-Lune", href: "/architecte-interieur-tassin-la-demi-lune" },
                { label: "Sainte-Foy-lès-Lyon", href: "/architecte-interieur-sainte-foy-les-lyon" },
                { label: "Charbonnières-les-Bains", href: "/architecte-interieur-charbonnieres-les-bains" },
                { label: "Oullins", href: "/architecte-interieur-oullins" },
                { label: "Brindas", href: "/architecte-interieur-brindas" },
                { label: "Craponne", href: "/architecte-interieur-craponne" },
              ].map((link) => (
                <li key={link.href}>
                  <Link
                    to={link.href}
                    className="text-sm text-primary-foreground/70 hover:text-primary-foreground transition-colors font-body"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="font-body text-xs uppercase tracking-widest mb-4 text-primary-foreground/75">Newsletter</h4>
            <p className="text-sm text-primary-foreground/70 font-body mb-4 leading-relaxed">
              Recevez nos inspirations déco et nos dernières réalisations.
            </p>
            {subscribed ? (
              <p className="text-sm text-rose font-body">Merci pour votre inscription ! ✨</p>
            ) : (
              <form onSubmit={handleSubscribe} className="flex gap-2">
                <input
                  type="email"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  placeholder="Votre email"
                  aria-label="Adresse email pour la newsletter"
                  required
                  maxLength={255}
                  className="flex-1 bg-primary-foreground/10 border border-primary-foreground/20 rounded px-3 py-2 text-sm text-primary-foreground placeholder:text-primary-foreground/70 font-body focus:outline-hidden focus:border-rose"
                />
                <button
                  type="submit"
                  disabled={subscribing}
                  className="bg-rose text-primary px-4 py-2 rounded text-sm font-body hover:bg-rose/90 transition-colors disabled:opacity-50"
                >
                  {subscribing ? "..." : "OK"}
                </button>
              </form>
            )}
          </div>

          <div>
            <h4 className="font-body text-xs uppercase tracking-widest mb-4 text-primary-foreground/75">Contact</h4>
            <p className="text-sm text-primary-foreground/70 font-body leading-relaxed">
              Grézieu-la-Varenne<br />
              Ouest Lyonnais, France
            </p>
            <Link
              to="/contact"
              className="inline-block mt-4 text-sm text-rose border-b border-rose/30 hover:border-rose transition-colors font-body"
            >
              Demander un devis
            </Link>
            <div className="flex items-center gap-4 mt-6">
              {[
                { label: "Google", href: "https://g.page/r/CYSnMtQRkg54EBM", icon: (
                  <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24"><path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"/><path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/><path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/></svg>
                )},
                { label: "Instagram", href: "https://www.instagram.com/charline_archi_jiia_studio/", icon: (
                  <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/></svg>
                )},
                { label: "LinkedIn", href: "https://www.linkedin.com/company/jiia-studio/", icon: (
                  <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg>
                )},
                { label: "Houzz", href: "https://www.houzz.fr/professionnels/architecte-d-interieur/jiia-studio-pfvwfr-pf~1548974661", icon: (
                  <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24"><path d="M12.5 3L5 7v5l3.5-2V7l4-2.5L16 7v3l3.5 2V7L12.5 3zM5 12v5l7.5 4L20 17v-5l-7.5 4L5 12z"/></svg>
                )},
                { label: "Archidvisor", href: "https://www.archidvisor.com/professionnels/architectes-d-interieur/auvergne-rhone-alpes/rhone/grezieu-la-varenne-69/jiia-studio", icon: (
                  <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2L2 19h4l6-11 6 11h4L12 2zm0 6l3.5 7h-7L12 8z"/></svg>
                )},
              ].map((social) => (
                <a
                  key={social.label}
                  href={social.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={social.label}
                  onClick={() => analytics.socialClick(social.label)}
                  className="text-primary-foreground/75 hover:text-primary-foreground transition-colors"
                >
                  {social.icon}
                </a>
              ))}
            </div>
          </div>
        </div>

        <div className="mt-16 pt-8 border-t border-primary-foreground/10 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-xs text-primary-foreground/70 font-body">
            © {new Date().getFullYear()} JIIA Studio - Tous droits réservés
          </p>
          <div className="flex items-center gap-4">
            <Link to="/mentions-legales" className="text-xs text-primary-foreground/70 hover:text-primary-foreground/70 transition-colors font-body">
              Mentions légales
            </Link>
            <Link to="/politique-de-confidentialite" className="text-xs text-primary-foreground/70 hover:text-primary-foreground/70 transition-colors font-body">
              Politique de confidentialité
            </Link>
          </div>
          <p className="text-xs text-primary-foreground/70 font-body">
            Architecte d'intérieur · Lyon · Ouest Lyonnais
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
